import React, { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';


const DEFAULT_DEDUPE_FIELD = {
  id: 'customer_email',
  label: 'Email',
  type: 'email',
  required: true,
  requestKey: 'email',
  dedupe: true,
  maxLength: 254,
};

const makeField = (index) => ({
  id: `field_${index}_${Date.now()}`,
  label: '',
  type: 'text',
  required: false,
  requestKey: `field_${index}`,
  dedupe: false,
  maxLength: 255,
});

const RegistrationFormBuilder = ({ initialFields, onSave, saving = false }) => {
  const seed = useMemo(
    () => (initialFields?.length ? initialFields : [DEFAULT_DEDUPE_FIELD]),
    [initialFields],
  );
  const [fields, setFields] = useState(seed);
  const [error, setError] = useState('');

  const updateField = (index, patch) => {
    setFields((current) => current.map((field, i) => (i === index ? { ...field, ...patch } : field)));
  };

  const removeField = (index) => {
    setFields((current) => current.filter((_, i) => i !== index));
  };

  const save = () => {
    const normalized = fields.map((field) => ({
      ...field,
      label: field.label.trim(),
      requestKey: (field.requestKey || field.id).trim(),
    }));
    if (normalized.some((field) => !field.label)) {
      setError('Every field needs a label.');
      return;
    }
    if (!normalized.some((field) => field.dedupe)) {
      setError('Choose at least one field for duplicate detection.');
      return;
    }
    if (normalized.some((field) => !/^[a-z][a-z0-9_]{0,63}$/.test(field.id))) {
      setError('Field IDs must start with a lowercase letter and contain only letters, numbers, or underscores.');
      return;
    }
    if (normalized.some((field) => !field.requestKey || !Number.isInteger(field.maxLength) || field.maxLength < 1 || field.maxLength > 2000)) {
      setError('Every field needs a request key and a maximum length from 1 to 2000.');
      return;
    }
    if (normalized.some((field) => field.type === 'select' && !field.options?.length)) {
      setError('Every select field needs at least one option.');
      return;
    }
    if (normalized.some((field) => field.type === 'consent' && !field.consentVersion?.trim())) {
      setError('Every consent field needs versioned consent copy.');
      return;
    }
    if (normalized.some((field) => field.type === 'hidden' && field.staticValue === undefined)) {
      setError('Every hidden field needs a static value.');
      return;
    }
    setError('');
    onSave(normalized);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-gray-700">
        Agents enter registration inputs only. The <strong>founder response supplies the stable identifier used for attribution</strong>. Choose a non-secret field for local duplicate detection; Proovam never requests passwords or OTPs.
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className="grid gap-3 rounded-xl border border-gray-200 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label htmlFor={`field-label-${index}`}>Field {index + 1} label</Label>
            <Input
              id={`field-label-${index}`}
              aria-label={`Field ${index + 1} label`}
              value={field.label}
              onChange={(event) => updateField(index, { label: event.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor={`field-type-${index}`}>Type</Label>
            <select
              id={`field-type-${index}`}
              value={field.type}
              onChange={(event) => updateField(index, {
                type: event.target.value,
                dedupe: ['email', 'phone', 'text'].includes(event.target.value) ? field.dedupe : false,
                required: event.target.value === 'hidden' ? false : field.required,
              })}
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              <option value="text">Text</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="date">Date</option>
              <option value="select">Select</option>
              <option value="consent">Consent</option>
              <option value="hidden">Hidden/static</option>
            </select>
          </div>
          <div>
            <Label htmlFor={`field-key-${index}`}>Founder request key</Label>
            <Input
              id={`field-key-${index}`}
              value={field.requestKey || ''}
              onChange={(event) => updateField(index, { requestKey: event.target.value })}
              placeholder="e.g. customer.email"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor={`field-max-${index}`}>Maximum length</Label>
            <Input
              id={`field-max-${index}`}
              type="number"
              min="1"
              max="2000"
              value={field.maxLength || 255}
              onChange={(event) => updateField(index, { maxLength: Number(event.target.value) })}
              className="mt-1"
            />
          </div>
          {field.type === 'select' && (
            <div className="sm:col-span-2">
              <Label htmlFor={`field-options-${index}`}>Options (one per line)</Label>
              <textarea
                id={`field-options-${index}`}
                value={(field.options || []).join('\n')}
                onChange={(event) => updateField(index, {
                  options: event.target.value.split('\n').map((item) => item.trim()).filter(Boolean),
                })}
                className="mt-1 min-h-20 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
          )}
          {field.type === 'consent' && (
            <div className="sm:col-span-2">
              <Label htmlFor={`field-consent-${index}`}>Versioned consent copy</Label>
              <Input
                id={`field-consent-${index}`}
                value={field.consentVersion || ''}
                onChange={(event) => updateField(index, { consentVersion: event.target.value })}
                placeholder="e.g. marketing-consent-v1: I agree…"
                className="mt-1"
              />
            </div>
          )}
          {field.type === 'hidden' && (
            <div className="sm:col-span-2">
              <Label htmlFor={`field-static-${index}`}>Static value sent to founder</Label>
              <Input
                id={`field-static-${index}`}
                value={field.staticValue || ''}
                onChange={(event) => updateField(index, { staticValue: event.target.value })}
                className="mt-1"
              />
            </div>
          )}
          <div className="flex items-end gap-4 sm:col-span-2 lg:col-span-4">
            <label className="flex h-9 items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={Boolean(field.required)}
                disabled={field.type === 'hidden'}
                onChange={(event) => updateField(index, { required: event.target.checked })}
              />
              Required
            </label>
            <label className="flex h-9 items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={Boolean(field.dedupe)}
                disabled={!['email', 'phone', 'text'].includes(field.type)}
                onChange={(event) => updateField(index, { dedupe: event.target.checked })}
              />
              Use for duplicate detection
            </label>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Remove field ${index + 1}`}
              disabled={fields.length === 1}
              onClick={() => removeField(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}

      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="rounded-xl bg-gray-50 p-3 text-xs text-gray-600" aria-label="Founder request preview">
        <strong>Request preview</strong>
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(
          Object.fromEntries(fields.map((field) => [
            field.requestKey || field.id,
            field.type === 'hidden' ? field.staticValue || '' : `<${field.type}>`,
          ])),
          null,
          2,
        )}</pre>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => setFields((current) => [...current, makeField(current.length + 1)])}>
          <Plus className="mr-2 h-4 w-4" /> Add field
        </Button>
        <Button type="button" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save registration form'}
        </Button>
      </div>
    </div>
  );
};

export default RegistrationFormBuilder;
